/**
 * ChatMessageUINew component is designed to be used for streaming responses.
 * It handles the display and management of chat messages within a conversation.
 */

import ChatDownloader from '@/components/ChatDownloader';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import useLogging from '@/hooks/useLogging';
import type { LangChainJSON } from '@/types/ai/messages';
import type { APIResponse } from '@/types/components/converse/messages';
import type { ChatHistoryEntryType } from '@/types/converse/chatHistory';
import type { Message } from '@/types/converse/message';
import { streamLLMResponse } from '@/utils/ai/stream';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { createTimeoutSignal } from '@/utils/signalConfig';
import { Box, CircularProgress, Stack } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChatInitials from './InitialSuggestedQuestions';
import PromptInputBox from './PromptInputBox';
import RenderMessage from './RenderMessage';

// Add message role type
type MessageRole = 'user' | 'assistant' | 'system';

// Define step types for timeline
type StepType =
  | 'thinking_update'
  | 'thinking_progress'
  | 'data_update'
  | 'data_update_file'
  | 'todo_update'
  | 'response'
  | 'final_response';

// Define step interface
interface Step {
  type: StepType;
  content: string | Record<string, any> | null;
  error: Error | null;
  timestamp?: string;
  parentUpdate?: string; // Reference to parent thinking update for thinking_progress steps
}

// Define stream chunk interface for better type checking
interface StreamChunk {
  type: StepType | 'metadata' | 'final_response';
  content: any;
  error: Error | null;
  icon?: string;
}

// Helper function to get filename from URL
const getFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/');
    return pathSegments[pathSegments.length - 1] || 'document';
  } catch (e) {
    return 'document';
  }
};

// Helper function for generating a new message ID
const generateMessageId = (): string => {
  const uuid = crypto.randomUUID();

  const hexString = uuid.substring(0, 24);

  // Convert to hexadecimal
  const hexId = hexString.replace(/-/g, '');

  // Handle leading zeros
  return hexId.replace(/^(.{2})/g, '0$1');
};

interface ChatMessageUIProps {
  currentChatID: string;
  setCurrentChatID: Dispatch<SetStateAction<string>>;
}

interface AIMessageResponse {
  lc: number;
  type: string;
  id: string[];
  kwargs: {
    content: string;
    tool_calls: any[];
    invalid_tool_calls: any[];
    additional_kwargs: Record<string, any>;
    response_metadata: Record<string, any>;
  };
}

interface ResponseContent {
  response: AIMessageResponse;
  json: Record<string, any>;
}

function ChatMessageUINew({
  currentChatID,
  setCurrentChatID,
}: ChatMessageUIProps): JSX.Element {
  const { log } = useLogging();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const showLoading = useRef<boolean>(true);
  const chatHistory = useAlchemystStoreForAi((store) => store.chatHistory);
  const chatChannel = useAlchemystStoreForAi((store) => store.chatChannel);
  const setStoreState = useAlchemystStoreForAi((store) => store.setStoreState);
  const documentViewer = useAlchemystStoreForAi(
    (store) => store.documentViewer
  );
  const updateChatWithDiff = useAlchemystStoreForAi(
    (store) => store.updateChatWithDiff
  );
  const { chat } = useAlchemystStoreForAi((store) => ({ chat: store.chat }));
  const chatRecordings = useAlchemystStoreForAi(
    (store) => store.chatRecordings
  );

  useEffect(() => {
    console.log('HCAT RECORDINGS', chatRecordings);
    // console.log('CURRENT CHAT ID', currentChatID);

    // Send recordings to the API when they change and are not empty
    const sendRecordingsToAPI = async () => {
      // console.log('RECORDING SENT', currentChatID);
      if (
        chatRecordings &&
        Object.keys(chatRecordings).length > 0 &&
        currentChatID
      ) {
        try {
          // console.log('THE CHAT RECORDINGS', chatRecordings);
          const keys = Object.keys(chatRecordings);
          // Extract only the most recent messages from each recording
          // const latestMessages = Object.entries(chatRecordings).reduce((result: Record<string, any>, [id, recording]) => {
          //   // Get the snapshots with their timestamps
          //   const snapshots = recording.snapshots || [];

          //   if (snapshots.length === 0) {
          //     return result;
          //   }

          //   // Find the snapshot with the latest timestamp
          //   const sortedSnapshots = [...snapshots].sort((a, b) => {
          //     const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          //     const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          //     return timestampB - timestampA; // Sort in descending order (newest first)
          //   });

          //   // Get the most recent snapshot
          //   const latestSnapshot = sortedSnapshots[0];

          //   if (latestSnapshot && latestSnapshot.diff && latestSnapshot.diff.messages) {
          //     result[id] = {
          //       messages: latestSnapshot.diff.messages,
          //       timestamp: latestSnapshot.timestamp,
          //       lastUserMessage: latestSnapshot.diff.lastUserMessage
          //     };
          //   }

          //   return result;
          // }, {});

          // if (Object.keys(latestMessages).length === 0) {
          //   console.log('No latest messages to send');
          //   return;
          // }

          // console.log('Sending latest messages to API:', latestMessages);

          const lastSnapshot = chatRecordings[keys[0]].snapshots.at(-1);
          const lastMessage = lastSnapshot?.diff.messages?.at(-1);

          const latestMessageToBeSent = [
            {
              message: lastMessage.message,
              role: lastMessage.role,
              timestamp: new Date().toISOString(),
            },
          ];

          const response = await fetchWithRewrites(
            `/api/v1/replaysession/${currentChatID}`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                type: 'chat',
                latestMessages: latestMessageToBeSent,
              }),
              signal: createTimeoutSignal(),
            }
          );

          if (!response.ok) {
            throw new Error('Failed to update latest messages in API');
          }

          const result = await response.json();
          console.log('Successfully updated latest messages in API:', result);
        } catch (error) {
          console.error('Error updating latest messages in API:', error);
        }
      }
    };
    sendRecordingsToAPI();
  }, [chatRecordings, currentChatID]);

  // State for messages and UI controls
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [thinkingProcess, setThinkingProcess] = useState<string[]>([]);
  const [lastThinkingProcess, setLastThinkingProcess] = useState<string[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [selectedTuners, setSelectedTuners] = useState<string[]>([]);
  const [messageIndex, setMessageIndex] = useState<null | number>(null);
  const [messageThinkingStates, setMessageThinkingStates] = useState<
    Record<string, boolean>
  >({});
  const [isAnyMessageThinking, setIsAnyMessageThinking] =
    useState<boolean>(false);
  const [timelineSteps, setTimelineSteps] = useState<Record<string, Step[]>>(
    {}
  );
  // Track which message accordions are open
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
    {}
  );

  // Update isAnyMessageThinking when messages change
  useEffect(() => {
    setIsAnyMessageThinking(messages.some((msg) => msg.isThinking));
  }, [messages]);

  // Update openAccordions when thinking state changes - automatically show accordions for thinking messages
  useEffect(() => {
    const newOpenAccordions = { ...openAccordions };
    let changed = false;

    messages.forEach((message) => {
      if (message.id && message.isThinking && !newOpenAccordions[message.id]) {
        // Automatically open accordions for thinking messages
        newOpenAccordions[message.id] = true;
        changed = true;
        console.log(`Opening accordion for thinking message: ${message.id}`);
      } else if (
        message.id &&
        message.id in newOpenAccordions &&
        newOpenAccordions[message.id] &&
        !message.isThinking
      ) {
        // Automatically close accordions when a message is done thinking
        newOpenAccordions[message.id] = false;
        changed = true;
        console.log(`Closing accordion for completed message: ${message.id}`);
      }
    });

    if (changed) {
      console.log('Updated accordion states:', newOpenAccordions);
      setOpenAccordions(newOpenAccordions);
    }
  }, [messages]);

  const setChatHistory = (chats: ChatHistoryEntryType[]): void => {
    setStoreState({ chatHistory: chats });
  };

  // Handle accordion toggle from child components
  const handleAccordionToggle = useCallback(
    (messageId: string, isOpen: boolean) => {
      console.log('Parent handleAccordionToggle called with:', {
        messageId,
        isOpen,
      });
      if (messageId) {
        setOpenAccordions((prev) => {
          const newState = {
            ...prev,
            [messageId]: isOpen,
          };
          console.log('Updated openAccordions:', newState);
          return newState;
        });
      }
    },
    []
  );

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Clear thinking process when thinking state changes
  useEffect(() => {
    if (!isAnyMessageThinking) {
      setThinkingProcess([]);
    }
  }, [isAnyMessageThinking]);

  /**
   * Fetches chat history data from the API and updates state.
   */
  // const fetchHistory = async () => {
  //   try {
  //     const chatHistoryResponse = await fetchWithRewrites(
  //       `/api/v1/chat/history?sources=${chatChannel}`,
  //       {
  //         signal: createTimeoutSignal(),
  //       }
  //     );
  //     const chatHistoryJson: { chats: ChatHistoryEntryType[] } =
  //       await chatHistoryResponse.json();
  //     console.log('Received chat history from /api/v1/chat/history = ');
  //     console.log(chatHistoryJson.chats ?? []);

  //     // Filter out duplicate untitled chats that have the same timestamp
  //     const uniqueChats = filterDuplicateUntitledChats(
  //       chatHistoryJson.chats ?? []
  //     );
  //     setChatHistory(uniqueChats);
  //   } catch (error) {
  //     setChatHistory([]);
  //   }
  // };

  // const [page, setPage] = useState(1);
  // const [hasMore, setHasMore] = useState(true);

  // const fetchHistory = async (pageToFetch = 1) => {
  //   if (chatHistoryLoading || !hasMore) return;

  //   try {
  //     setChatHistoryLoading(true);
  //     const chatHistoryResponse = await fetchWithRewrites(
  //       `/api/v1/chat/history?sources=${chatChannel}&page=${pageToFetch}&limit=10`,
  //       {
  //         signal: createTimeoutSignal(),
  //       }
  //     );

  //     const chatHistoryJson: {
  //       chats: ChatHistoryEntryType[];
  //       pagination: {
  //         page: number;
  //         limit: number;
  //         hasMore: boolean;
  //         total: number;
  //       };
  //     } = await chatHistoryResponse.json();

  //     console.log('Received chat history from /api/v1/chat/history = ');
  //     console.log(chatHistoryJson.chats ?? []);

  //     // Filter out duplicate untitled chats (per your logic)
  //     const filteredChats = filterDuplicateUntitledChats(
  //       chatHistoryJson.chats ?? []
  //     );

  //     // Append to existing chat history
  //     console.log('THE FILTERED CHATS', filteredChats);
  //     setChatHistory(filteredChats);

  //     // Update pagination state
  //     setPage(pageToFetch);
  //     setHasMore(chatHistoryJson.pagination.hasMore);
  //   } catch (error) {
  //     console.error('Failed to fetch chat history:', error);
  //     if (pageToFetch === 1) {
  //       setChatHistory([]);
  //     }
  //     setHasMore(false);
  //   } finally {
  //     setChatHistoryLoading(false);
  //   }
  // };

  // // Helper function to filter out duplicate untitled chats

  // const filterDuplicateUntitledChats = (
  //   chats: ChatHistoryEntryType[]
  // ): ChatHistoryEntryType[] => {
  //   // Separate untitled chats from normal chats
  //   const untitledChats: ChatHistoryEntryType[] = [];
  //   const normalChats: ChatHistoryEntryType[] = [];

  //   chats.forEach((chat) => {
  //     if (chat.title.toLowerCase().includes('untitled chat at')) {
  //       untitledChats.push(chat);
  //     } else {
  //       normalChats.push(chat);
  //     }
  //   });

  //   // Keep only the most recent untitled chat
  //   let mostRecentUntitledChat: ChatHistoryEntryType | null = null;

  //   if (untitledChats.length > 0) {
  //     mostRecentUntitledChat = untitledChats.reduce((latest, current) =>
  //       current.timestamp > latest.timestamp ? current : latest
  //     );
  //   }

  //   // Combine normal chats with the most recent untitled chat (if any)
  //   return mostRecentUntitledChat
  //     ? [...normalChats, mostRecentUntitledChat]
  //     : normalChats;
  // };

  // const fetchHistoryCallback = useCallback(fetchHistory, [chatChannel]);

  // // Fetch chat history on initial render and when channel changes
  // useEffect(() => {
  //   fetchHistoryCallback();
  // }, [fetchHistoryCallback, chatChannel]);
  // handle generating title bassed on the chat history
  const generateResponse = useCallback(
    async (chat_history: LangChainJSON[]): Promise<APIResponse> => {
      const chatObject = {
        chat_history,
        chatId: (currentChatID ?? [].length > 0) ? currentChatID : undefined,
        researchIcps: chat.tools.researchIcps,
        stream: false,
        voice: false,
      };

      console.log('Chat Object = ', chatObject);
      const res = await fetchWithRewrites('/api/v1/chat/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chatObject),
        signal: createTimeoutSignal(),
      });

      console.log('Result status = ', res.status);

      if (!res.ok) {
        throw new Error('Failed to generate response');
      }

      const response: APIResponse = await res.json();
      return response;
    },
    [currentChatID, chat.tools.researchIcps]
  );
  // Fetch messages for a specific chat
  const fetchMessages = useCallback(async (id: string) => {
    showLoading.current && setLoadingHistory(true);
    try {
      const messagesResponse = await fetchWithRewrites(
        `/api/v1/chat/fetch/${id}`,
        {
          method: 'GET',
          signal: createTimeoutSignal(),
        }
      );
      const messagesJson: { messages: Message[] } =
        await messagesResponse.json();

      console.log('Fetched message history:', messagesJson.messages);

      // Set messages directly without any de-duplication
      setMessages(messagesJson.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
    setLoadingHistory(false);
  }, []);

  const onSend = useCallback(
    async (direct_message?: string) => {
      console.log(
        'OnSend called from ChatMessageUINew with message:',
        direct_message
      );
      const _message = direct_message ? direct_message : currentMessage;
      if (!_message.trim() || isAnyMessageThinking) return;

      // Clear thinking process states when starting a new message
      setThinkingProcess([]);
      setLastThinkingProcess([]);

      // Add user message
      const userMessageId = generateMessageId();
      const updatedMessages = [
        ...messages,
        {
          id: userMessageId,
          role: 'user' as MessageRole,
          message: _message,
          json: '',
        },
      ];

      // Record the user message in the diff tracker if we have a chat ID
      if (currentChatID) {
        updateChatWithDiff(currentChatID, {
          messages: updatedMessages,
          lastUserMessage: _message,
          timestamp: new Date().toISOString(),
        });
      }

      setMessages(updatedMessages);
      setCurrentMessage('');

      try {
        // const langchainMessages = messages.map((msg) => ({
        //   lc: 0,
        //   type: msg.role === 'user' ? 'human' : 'ai',
        //   id: [],
        //   lc_kwargs: {
        //     content: msg.message,
        //     response_metadata: {},
        //     tool_calls: [],
        //     invalid_tool_calls: [],
        //     additional_kwargs: {
        //       json: msg.json ? JSON.parse(msg.json) : {},
        //     },
        //   },
        // }));

        const langchainMessages = messages.map((msg) => ({
          content: msg.message,
          role: msg.role,
        }));

        // Add the new message to langchainMessages
        // langchainMessages.push({
        //   lc: 0,
        //   type: 'human',
        //   id: [],
        //   lc_kwargs: {
        //     content: _message,
        //     response_metadata: {},
        //     tool_calls: [],
        //     invalid_tool_calls: [],
        //     additional_kwargs: {
        //       json: {},
        //     },
        //   },
        // });

        langchainMessages.push({ content: _message, role: 'user' });

        console.log('Tools being sent in streamLLMResponse = ', chat.tools);
        const stream = streamLLMResponse({
          chat_history: langchainMessages,
          chatId: currentChatID || undefined,
          tools: chat.tools,
          stream: true,
          voice: false,
        });

        // Add a temporary assistant message for streaming with thinking state
        const assistantMessageId = generateMessageId();
        const assistantMessage = {
          id: assistantMessageId,
          role: 'assistant',
          message: '',
          json: '',
          isThinking: true,
        } satisfies Message;

        setMessages([...updatedMessages, assistantMessage]);

        // Track this message's thinking state
        setMessageThinkingStates((prev) => ({
          ...prev,
          [assistantMessageId]: true,
        }));

        // Initialize timeline steps for this message
        setTimelineSteps((prev) => ({
          ...prev,
          [assistantMessageId]: [],
        }));

        // Ensure accordion is open for thinking message
        setOpenAccordions((prev) => ({
          ...prev,
          [assistantMessageId]: true,
        }));

        let fullResponse = '';
        let lastThinkingUpdate = '';
        let hasReceivedResponse = false;
        let messageThinkingProcess: string[] = [];
        let currentMessageTimeline: Step[] = [];

        // Helper function to add step to timeline
        const addTimelineStep = (step: Step) => {
          currentMessageTimeline = [
            ...currentMessageTimeline,
            {
              ...step,
              timestamp: new Date().toISOString(),
            },
          ];

          setTimelineSteps((prev) => ({
            ...prev,
            [assistantMessageId]: currentMessageTimeline,
          }));
        };

        for await (const chunk of stream) {
          // Safely handle chunks with proper type checking
          const safeChunk = chunk as unknown as StreamChunk;

          if (safeChunk.type === 'thinking_update') {
            if (safeChunk.content) {
              const content = safeChunk.content;
              setThinkingProcess((prev) => {
                if (prev.includes(content)) {
                  return prev;
                }
                return [...prev, content];
              });
              messageThinkingProcess = [...messageThinkingProcess, content];
              lastThinkingUpdate = content;
              setLastThinkingProcess((prev) => {
                if (prev.includes(content)) {
                  return prev;
                }
                return [...prev, content];
              });

              // Add to timeline
              if (content !== '[DONE]') {
                addTimelineStep({
                  type: 'thinking_update',
                  content,
                  error: null,
                });
              }
            }
          } else if (safeChunk.type === 'thinking_progress') {
            // Handle thinking progress updates - associate with the most recent thinking_update
            if (safeChunk.content) {
              console.log('Received thinking progress:', safeChunk.content);

              // Keep this progress step connected to the most recent update
              // We'll add metadata to help RenderMessage understand which update it belongs to
              addTimelineStep({
                type: 'thinking_progress',
                content: safeChunk.content,
                error: null,
                // Add reference to the latest thinking update to help with grouping
                parentUpdate: lastThinkingUpdate,
              });
            }
          } else if (safeChunk.type === 'metadata') {
            const { chatId, title } = safeChunk.content;

            // Create new chat if needed
            if (!currentChatID && chatId) {
              const newChat = {
                id: chatId,
                title,
                timestamp: Date.now(),
              };
              setChatHistory([...chatHistory, newChat]);
              setCurrentChatID(chatId);
            }
          } else if (
            safeChunk.type === 'data_update_file' &&
            safeChunk.content &&
            !safeChunk.error
          ) {
            // Handle file URL or code content from the chat stream
            console.log('Received file content:', safeChunk.content);
            const currentFileUrls = documentViewer.fileUrls || [];
            const newFileIndex = currentFileUrls.length;

            // Check if the content is a URL or a long piece of code
            const contentToAdd = safeChunk.content.toString();

            // Check if this file is already in the viewer using exact URL matching
            const existingIndex = currentFileUrls.findIndex(
              (url) => url === contentToAdd
            );

            // Only add if it's not already in the viewer
            if (existingIndex === -1) {
              const updatedViewer = {
                ...documentViewer,
                fileUrls: [...currentFileUrls, contentToAdd],
                isOpen: true,
                activeTabIndex: newFileIndex,
              };

              setStoreState({
                documentViewer: updatedViewer,
              });
            } else {
              // If file exists, just switch to its tab
              const updatedViewer = {
                ...documentViewer,
                isOpen: true,
                activeTabIndex: existingIndex,
              };

              setStoreState({
                documentViewer: updatedViewer,
              });
            }

            // Add to timeline
            addTimelineStep({
              type: 'data_update_file',
              content: contentToAdd,
              error: null,
            });
          } else if (safeChunk.type === 'data_update') {
            // Handle data update from the chat stream
            console.log('Received data update:', safeChunk.content);
            const currentFileUrls = documentViewer.fileUrls || [];
            const newFileIndex = currentFileUrls.length;

            // Check if the content is valid
            const contentToAdd = safeChunk.content
              ? safeChunk.content.toString()
              : '';
            if (contentToAdd) {
              const updatedViewer = {
                ...documentViewer,
                fileUrls: [...currentFileUrls, contentToAdd],
                isOpen: true,
                activeTabIndex: newFileIndex,
              };

              setStoreState({
                documentViewer: updatedViewer,
              });
            }

            // Add to timeline
            addTimelineStep({
              type: 'data_update',
              content: safeChunk.content || null,
              error: null,
            });
          } else if (
            safeChunk.type === 'todo_update' &&
            safeChunk.content &&
            !safeChunk.error
          ) {
            // Handle todo update from the chat stream
            console.log('Received todo update:', safeChunk.content);

            // Ensure documentViewer.isOpen is true and has correct properties
            // The ChatMainLayout will handle showing the document viewer when isOpen is true
            setStoreState({
              documentViewer: {
                ...documentViewer,
                isOpen: true,
                fileUrls: documentViewer.fileUrls || [],
              },
            });

            // Then update the scratchpad content in a separate state update
            // This ensures the doc side sheet opens before updating the scratchpad
            setTimeout(() => {
              // Force-trigger document update to ensure side panel is open
              const forceDocViewerUpdate = {
                ...documentViewer,
                scratchpadContent: safeChunk.content,
                isOpen: true,
                // Adding a dummy empty string URL if no URLs exist to ensure the document viewer shows
                fileUrls: documentViewer.fileUrls?.length
                  ? documentViewer.fileUrls
                  : [''],
              };

              setStoreState({
                documentViewer: forceDocViewerUpdate,
              });
            }, 300); // Increase timeout to give the panel time to open

            // Add to timeline
            addTimelineStep({
              type: 'todo_update',
              content: safeChunk.content,
              error: null,
            });
          } else if (safeChunk.type === 'response' && safeChunk.content) {
            const responseContent = safeChunk.content;
            // // @ts-expect-error
            if (responseContent?.response.kwargs.content) {
              hasReceivedResponse = true;
              // // @ts-expect-error
              fullResponse = responseContent.response.kwargs.content;
              const jsonData = responseContent.json || {};

              // Add final response to timeline
              addTimelineStep({
                type: 'response',
                content: fullResponse,
                error: null,
              });

              // Update the message thinking state
              setMessageThinkingStates((prev) => ({
                ...prev,
                [assistantMessageId]: false,
              }));

              // Update the assistant message with the response and thinking steps
              setMessages((prevMessages) => {
                const updatedMessages = [...prevMessages];
                const assistantMessageIndex = updatedMessages.findIndex(
                  (msg) => msg.id === assistantMessageId
                );

                if (assistantMessageIndex !== -1) {
                  // Separate thinking_update and thinking_progress steps
                  const thinkingUpdates = messageThinkingProcess.map(
                    (content) => ({
                      type: 'thinking_update',
                      content,
                      error: null,
                    })
                  );

                  // Get thinking_progress steps from the timeline
                  const thinkingProgress = currentMessageTimeline.filter(
                    (step) => step.type === 'thinking_progress'
                  );

                  updatedMessages[assistantMessageIndex] = {
                    ...updatedMessages[assistantMessageIndex],
                    message: fullResponse,
                    json: JSON.stringify(jsonData),
                    isThinking: false,
                    thinking_steps: thinkingUpdates,
                    steps: currentMessageTimeline,
                  };
                }

                return updatedMessages;
              });

              scrollToBottom();

              // Record the AI response in the diff tracker if we have a chat ID
              if (currentChatID) {
                // Separate thinking_update and thinking_progress steps
                const thinkingUpdates = messageThinkingProcess.map(
                  (content) => ({
                    type: 'thinking_update',
                    content,
                    error: null,
                  })
                );

                updateChatWithDiff(currentChatID, {
                  messages: messages.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          message: fullResponse,
                          json: JSON.stringify(jsonData),
                          isThinking: false,
                          thinking_steps: thinkingUpdates,
                          steps: currentMessageTimeline,
                        }
                      : msg
                  ),
                  lastAIResponse: fullResponse,
                  timestamp: new Date().toISOString(),
                });
              }
            }
          } else if (safeChunk.type === 'final_response' && safeChunk.content) {
            // Handle final_response type that comes from backend
            hasReceivedResponse = true;
            fullResponse = safeChunk.content.content || '';
            const jsonData = safeChunk.content.json || {};

            // Add final response to timeline
            addTimelineStep({
              type: 'response',
              content: fullResponse,
              error: null,
            });

            // Update the message thinking state
            setMessageThinkingStates((prev) => ({
              ...prev,
              [assistantMessageId]: false,
            }));

            // Update the assistant message with the response and thinking steps
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              const assistantMessageIndex = updatedMessages.findIndex(
                (msg) => msg.id === assistantMessageId
              );

              if (assistantMessageIndex !== -1) {
                // Separate thinking_update and thinking_progress steps
                const thinkingUpdates = messageThinkingProcess.map(
                  (content) => ({
                    type: 'thinking_update',
                    content,
                    error: null,
                  })
                );

                updatedMessages[assistantMessageIndex] = {
                  ...updatedMessages[assistantMessageIndex],
                  message: fullResponse,
                  json: JSON.stringify(jsonData),
                  isThinking: false,
                  thinking_steps: thinkingUpdates,
                  steps: currentMessageTimeline,
                };
              }

              return updatedMessages;
            });

            scrollToBottom();

            // Record the AI response in the diff tracker if we have a chat ID
            if (currentChatID) {
              // Separate thinking_update and thinking_progress steps
              const thinkingUpdates = messageThinkingProcess.map((content) => ({
                type: 'thinking_update',
                content,
                error: null,
              }));

              updateChatWithDiff(currentChatID, {
                messages: messages.map((msg) =>
                  msg.id === assistantMessageId
                    ? {
                        ...msg,
                        message: fullResponse,
                        json: JSON.stringify(jsonData),
                        isThinking: false,
                        thinking_steps: thinkingUpdates,
                        steps: currentMessageTimeline,
                      }
                    : msg
                ),
                lastAIResponse: fullResponse,
                timestamp: new Date().toISOString(),
              });
            }
          }
        }

        if (!hasReceivedResponse) {
          throw new Error('No response received from the stream');
        }

        log(
          {
            message: 'Successfully generated response using LLM',
            timestamp: new Date().toISOString(),
          },
          'platform/converse.llm-response'
        );

        // Refetch messages to ensure we have the latest data
        if (currentChatID) {
          await fetchMessages(currentChatID);
        }
      } catch (error) {
        console.error('Error during message processing:', error);

        // Find and update the thinking message
        setMessages((prev) => {
          const updatedMessages = [...prev];
          // Find the index of the thinking message
          const thinkingMessageIndex = updatedMessages.findIndex(
            (msg) => msg.isThinking
          );

          if (thinkingMessageIndex !== -1) {
            // Remove the thinking message
            updatedMessages.splice(thinkingMessageIndex, 1);
          }

          // Add error message
          const errorMessageId = generateMessageId();
          updatedMessages.push({
            id: errorMessageId,
            role: 'assistant' as MessageRole,
            message:
              'Sorry, I am unable to process your request at the moment. Please try again later.',
            json: '',
            isThinking: false,
          });

          return updatedMessages;
        });

        // Clear all thinking states after an error
        setMessageThinkingStates({});
        setThinkingProcess([]);
        setLastThinkingProcess([]);
        setIsAnyMessageThinking(false);
        setTimelineSteps({});
      } finally {
        scrollToBottom();
      }
    },
    [
      currentMessage,
      messages,
      isAnyMessageThinking,
      currentChatID,
      setChatHistory,
      chatHistory,
      chat.tools.researchIcps,
      scrollToBottom,
      log,
      updateChatWithDiff,
    ]
  );

  // Load messages when chat ID changes
  useEffect(() => {
    if (currentChatID) {
      fetchMessages(currentChatID);
    } else {
      setMessages([]);
    }
  }, [currentChatID, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const renderedMessages = useMemo(() => {
    // Add a timestamp counter for duplicate message IDs to ensure each gets a unique key
    const idCounter = new Map<string, number>();

    return messages.map((message, index) => {
      // Create a unique key for each message, even if they have the same ID
      let uniqueKey = message.id || `msg-${index}`;
      if (message.id && idCounter.has(message.id)) {
        const count = idCounter.get(message.id)! + 1;
        idCounter.set(message.id, count);
        uniqueKey = `${message.id}-${count}`;
      } else if (message.id) {
        idCounter.set(message.id, 0);
      }

      return (
        <Box key={uniqueKey} width="100%">
          {message.role === 'assistant' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                // margin: '0 auto',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              <RenderMessage
                id={message.id}
                animate={false}
                currentChatId={currentChatID}
                isAccordionOpen={
                  message.id ? openAccordions[message.id] || false : false
                }
                isThinking={message.isThinking}
                json={message.json}
                message={message.message}
                messageId={message.id || undefined}
                onAccordionToggle={
                  message.id
                    ? (messageId, isOpen) => {
                        console.log('Accordion toggled in parent', {
                          messageId,
                          isOpen,
                        });
                        if (message.id)
                          handleAccordionToggle(message.id, isOpen);
                      }
                    : undefined
                }
                role={message.role}
                steps={
                  (message.steps ||
                    (message.id ? timelineSteps[message.id] : []) ||
                    []) as never
                }
                thinkingProcess={
                  message.isThinking ? thinkingProcess : undefined
                }
                thinking_steps={
                  (!message.isThinking
                    ? (message.thinking_steps ?? undefined)
                    : undefined) as never
                }
              />
            </Box>
          )}
          {message.role === 'user' && (
            <RenderMessage
              animate={false}
              currentChatId={currentChatID}
              json={message.json}
              message={message.message}
              role={message.role}
              messageId={message.id || undefined}
              id={message.id || crypto.randomUUID()}
              thinking_steps={message.thinking_steps as never}
            />
          )}
        </Box>
      );
    });
  }, [
    messages,
    thinkingProcess,
    timelineSteps,
    openAccordions,
    handleAccordionToggle,
    currentChatID,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '97.5vh',
        marginX: 12,
      }}
    >
      {loadingHistory && currentChatID !== '' ? (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {(messages ?? []).length > 0 ? (
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <ChatDownloader chatId={currentChatID} messages={messages} />
              </Box>
              <Stack spacing={2}>
                {renderedMessages}
                <div ref={chatEndRef} />
              </Stack>
            </Box>
          ) : (
            <ChatInitials onSend={onSend} />
          )}
        </>
      )}
      <Box sx={{ paddingTop: 1, position: 'sticky', bottom: 0 }}>
        <PromptInputBox
          onSend={onSend}
          setThinking={setIsAnyMessageThinking}
          setValue={setCurrentMessage}
          thinking={isAnyMessageThinking}
          value={currentMessage}
        />
      </Box>
    </Box>
  );
}

export default ChatMessageUINew;
